/**
 * The alert panel — everything the alert screen shows below its navigation,
 * factored out so the ticket screen embeds the same thing (task 172,
 * ADR-0027 §4): the header (BSM id, severity, status, disposition, score,
 * advisory title), the timing strip, the triage controls, the Response
 * panel, the member detections and the alert timeline.
 *
 * Every control calls the same MDR routes whichever screen renders it, and
 * follows the user's permissions on the alert (`triage`, `respond`) — a
 * response request from a ticket is the same request through the same
 * response layer and rails (CLAUDE.md rule 10). `onChanged` lets the host
 * refresh what it shows beside the alert (the ticket screen's ticket, which
 * the ticketing consumer updates from the alert's events).
 *
 * The rest of the alert-screen notes (Stories 8, 14, 16, 17): triage
 * controls offer only the moves Story 8's table allows from the current
 * status, and are hidden for viewer — the API refuses them anyway; hiding
 * them is courtesy, not security. Members are the detections the alert
 * groups, each with its own AI panel and its decision trace rendered as
 * "considered → fired". The timeline is the alert's `EVT#` items, with
 * system actors visually distinct from people. A comment is a note that does
 * not move the alert — legal in every status, closed included.
 *
 * Embedded on the Tickets app's ticket screen (bluesphere-ticketing ADR-0008,
 * task 191b), the panel is mounted with `showTimeline={false}` and
 * `showTriage={false}`: that screen shows one timeline merging the alert's
 * events with the ticket's (rendered with `AlertTimelineEntry`, so an alert
 * entry looks the same on both screens), and its own Start work, Resolve,
 * False positive and Reopen move the alert and the ticket together through
 * Ticketing's API. Both default to true, so MDR's alert screen is unchanged.
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';

import {
  ApiError,
  TRANSITIONS,
  addAlertComment,
  transitionAlert,
  type Alert,
  type AlertDetail as AlertDetailData,
  type AlertEvent,
  type AlertMember,
  type AlertStatus,
  type DecisionTrace,
  type Timing,
} from '@bluesphere/api-client';
import { PillButton, SEVERITY_COLORS, SeverityChip } from '@bluesphere/ui-kit';

import { MdrLink, useMdrToken } from './context.js';
import { AiPanel } from './AiPanel.js';
import { AiTitle } from './AiTitle.js';
import { AssetContextPanel, namesEndpoint } from './AssetContextPanel.js';
import { OutreachPanel } from './OutreachPanel.js';
import { ResponsePanel } from './ResponsePanel.js';
import { ErrorState, GroupBadge, SourceBadge } from './states.js';
import {
  absoluteTime,
  actionLabel,
  dispositionLabel,
  duration,
  eventLabel,
  outreachDetail,
  outcomeLabel,
  sourceLabel,
  statusLabel,
  timeAgo,
} from './format.js';

const TRANSITION_LABELS: Record<AlertStatus, string> = {
  open: 'Reopen',
  triaged: 'Mark triaged',
  in_progress: 'Start work',
  resolved: 'Resolve',
  false_positive: 'False positive',
};

export function AlertPanel({
  tenantId,
  alertRef,
  data,
  detailKey,
  canTriage,
  canRespond,
  onChanged,
  showTimeline = true,
  showTriage = true,
}: {
  tenantId: string;
  alertRef: string;
  data: AlertDetailData;
  detailKey: unknown[];
  canTriage: boolean;
  canRespond: boolean;
  onChanged?: () => void;
  /** The alert's own timeline and comment box; false where the host merges the timeline. */
  showTimeline?: boolean;
  /** The status moves; false where the host moves the alert with its ticket. */
  showTriage?: boolean;
}) {
  const {
    alert: a,
    members,
    events,
    timing,
    ai_title: aiTitle,
    actions,
    withheld,
    available_actions: availableActions,
  } = data;
  const severityColor = SEVERITY_COLORS[a.severity];
  // One Asset context panel per endpoint the members name, matched as the
  // API matches them: case and domain do not make a second machine.
  const endpoints = [
    ...new Map(
      members
        .map((m) => m.detection)
        .filter(namesEndpoint)
        .map((d) => [
          (d.asset_name as string).trim().toLowerCase().split('.')[0],
          d.asset_name as string,
        ]),
    ).values(),
  ];

  return (
    <div className="space-y-6">
      <div className="bs-card p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <span className="font-mono text-sm font-semibold text-bluesphere-primary">
                {a.short_id}
              </span>
              <SeverityChip severity={a.severity} />
              <StatusChip status={a.status} />
              <span className="bs-chip bg-bluesphere-surfaceMuted text-bluesphere-textOnLight">
                {dispositionLabel(a.disposition)}
              </span>
              {a.sources.map((s) => (
                <SourceBadge key={s} label={sourceLabel(s)} />
              ))}
              {(a.groups ?? []).map((g) => (
                <GroupBadge key={g} slug={g} />
              ))}
              {a.backfill && <span className="text-xs text-bluesphere-muted">back-fill</span>}
            </div>
            {/* The deterministic title: the earliest member's (task 77a).
                "… and N more" says it is one member's story of several. */}
            <h1 className="text-xl font-bold lg:text-2xl">
              {a.title}
              {a.member_count > 1 && (
                <span className="font-normal text-bluesphere-muted">
                  {' '}… and {a.member_count - 1} more
                </span>
              )}
            </h1>
            <AiTitle
              tenantId={tenantId}
              alertRef={alertRef}
              aiTitle={aiTitle}
              canRequest={canTriage}
              refreshKey={detailKey}
            />
            <p className="mt-1 text-sm text-bluesphere-muted">
              {a.member_count} detection{a.member_count === 1 ? '' : 's'}
              {a.asset_names.length > 0 && ` · ${a.asset_names.join(', ')}`}
              {a.incident_id && (
                <>
                  {' · incident '}
                  <span className="break-all font-mono">{a.incident_id}</span>
                </>
              )}
            </p>
          </div>
          <div className="shrink-0 text-right">
            <div
              className="text-4xl font-bold leading-none tabular-nums"
              style={{ color: severityColor }}
            >
              {a.score}
            </div>
            <div className="mt-1 text-xs text-bluesphere-muted">score</div>
          </div>
        </div>

        <TimingStrip alert={a} timing={timing} />

        {!showTriage ? null : canTriage ? (
          <TriageControls alert={a} alertRef={alertRef} tenantId={tenantId} onChanged={onChanged} />
        ) : (
          <p className="mt-4 border-t border-bluesphere-border pt-3 text-xs text-bluesphere-muted">
            Your role can view this alert but not change its status.
          </p>
        )}

        <ResponsePanel
          tenantId={tenantId}
          alertRef={alertRef}
          detailKey={detailKey}
          available={availableActions}
          actions={actions}
          withheld={withheld}
          canRequest={canRespond}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className={showTimeline ? 'space-y-6 lg:col-span-2' : 'space-y-6 lg:col-span-3'}>
          {data.outreach && <OutreachPanel outreach={data.outreach} />}
          <Panel title={members.length === 1 ? 'Detection' : `Detections (${members.length})`}>
            {members.length === 0 ? (
              <p className="py-6 text-center text-sm text-bluesphere-muted">
                No member detections could be read.
              </p>
            ) : (
              <div className="space-y-4">
                {members.map((m) => (
                  <MemberCard
                    key={m.detection.detection_id}
                    member={m}
                    tenantId={tenantId}
                    canRequest={canTriage}
                    refreshKey={detailKey}
                  />
                ))}
              </div>
            )}
          </Panel>

          {endpoints.map((asset) => (
            <AssetContextPanel key={asset} tenantId={tenantId} asset={asset} />
          ))}

          {a.related_alert_ids.length > 0 && (
            <Panel title="Related alerts">
              <p className="mb-2 text-xs text-bluesphere-muted">
                Earlier alerts for the same provider incident whose reopen window had passed.
              </p>
              <ul className="space-y-1 text-sm">
                {a.related_alert_ids.map((id) => (
                  <li key={id}>
                    <MdrLink
                      target={{ to: 'alert', alertRef: id }}
                      className="break-all font-mono text-xs underline"
                    >
                      {id}
                    </MdrLink>
                  </li>
                ))}
              </ul>
            </Panel>
          )}
        </div>

        {showTimeline && (
          <div className="lg:col-span-1">
            <Panel title="Timeline">
              {canTriage && (
                <CommentForm alertRef={alertRef} tenantId={tenantId} onChanged={onChanged} />
              )}
              {events.length === 0 ? (
                <p className="py-6 text-center text-sm text-bluesphere-muted">No events yet.</p>
              ) : (
                <ol className="space-y-3">
                  {events
                    .slice()
                    .reverse()
                    .map((e) => (
                      <AlertTimelineEntry key={e.event_id} event={e} />
                    ))}
                </ol>
              )}
            </Panel>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- Timing (Story 17) ----------

function TimingStrip({ alert: a, timing }: { alert: Alert; timing: Timing }) {
  return (
    <dl className="mt-5 grid grid-cols-2 gap-x-6 gap-y-2 border-t border-bluesphere-border pt-4 text-sm md:grid-cols-4 lg:grid-cols-7">
      <Field
        label="Detected"
        value={<span title={a.detected_at}>{absoluteTime(a.detected_at)}</span>}
      />
      <Field
        label="TTD"
        hint="Provider detection to our first write"
        value={duration(timing.ttd_seconds)}
      />
      <Field
        label="TTT"
        hint="Detection to the playbook's conclusion"
        value={duration(timing.ttt_seconds)}
      />
      <Field
        label="TTC"
        hint="Detection to closure; empty while open"
        value={duration(timing.ttc_seconds)}
      />
      <Field
        label="Closed"
        value={
          a.closed_at ? `${absoluteTime(a.closed_at)} (${a.resolution_source ?? 'analyst'})` : '—'
        }
      />
      <Field label="Reopened" value={a.reopen_count > 0 ? `${a.reopen_count}×` : '—'} />
      <Field label="Updated" value={<span title={a.updated_at}>{timeAgo(a.updated_at)}</span>} />
    </dl>
  );
}

// ---------- Triage (Story 8) ----------

function TriageControls({
  alert: a,
  alertRef,
  tenantId,
  onChanged,
}: {
  alert: Alert;
  alertRef: string;
  tenantId: string;
  onChanged?: () => void;
}) {
  const idToken = useMdrToken();
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');
  const [pending, setPending] = useState<AlertStatus | null>(null);

  const mutation = useMutation({
    mutationFn: (to: AlertStatus) =>
      transitionAlert(tenantId, alertRef, { to, note: note.trim() || undefined }, idToken),
    onMutate: (to) => setPending(to),
    onSettled: () => setPending(null),
    onSuccess: () => {
      setNote('');
      // The detail, the queue and the Overview all show status.
      void queryClient.invalidateQueries({ queryKey: ['alert', tenantId, alertRef] });
      void queryClient.invalidateQueries({ queryKey: ['alerts', tenantId] });
      void queryClient.invalidateQueries({ queryKey: ['overview', tenantId] });
      onChanged?.();
    },
    onError: (error) => {
      // A 409 means someone else moved it; the fresh state is the answer.
      if (error instanceof ApiError && error.status === 409) {
        void queryClient.invalidateQueries({ queryKey: ['alert', tenantId, alertRef] });
      }
    },
  });

  const moves = TRANSITIONS[a.status] ?? [];
  const closing = (to: AlertStatus) => to === 'resolved' || to === 'false_positive';

  return (
    <div className="mt-4 border-t border-bluesphere-border pt-4">
      <div className="flex flex-wrap items-end gap-3">
        <label className="min-w-0 flex-1">
          <span className="mb-1 block text-xs uppercase tracking-wider text-bluesphere-muted">
            Note (optional, recorded with the status change)
          </span>
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={1000}
            placeholder="What you found, or why this is closed"
            className="w-full rounded-input border border-bluesphere-border bg-white px-3 py-1.5 text-sm text-bluesphere-textOnLight"
          />
        </label>
        <div className="flex flex-wrap gap-2">
          {moves.map((to) => (
            <PillButton
              key={to}
              size="sm"
              variant={to === 'false_positive' ? 'outline' : closing(to) ? 'secondary' : 'primary'}
              disabled={mutation.isPending}
              onClick={() => mutation.mutate(to)}
            >
              {pending === to ? 'Saving…' : TRANSITION_LABELS[to]}
            </PillButton>
          ))}
        </div>
      </div>
      {mutation.isError && (
        <div className="mt-3">
          <ErrorState title="The status did not change" error={mutation.error} />
        </div>
      )}
    </div>
  );
}

// ---------- Members ----------

function MemberCard({
  member,
  tenantId,
  canRequest,
  refreshKey,
}: {
  member: AlertMember;
  tenantId: string;
  canRequest: boolean;
  refreshKey: unknown[];
}) {
  const { detection: d, trace, ai } = member;
  return (
    <div className="rounded-card border border-bluesphere-border p-4">
      {/* The header opens the detection from anywhere on it; the analysis
          and trace below stay selectable and keep their own controls. */}
      <div className="relative flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="mb-1 flex flex-wrap items-center gap-2">
            <SeverityChip severity={d.severity} />
            <SourceBadge label={sourceLabel(d.source)} />
            {d.detection_name && <SourceBadge label={d.detection_name} />}
            <span className="text-xs text-bluesphere-muted" title={d.first_seen}>
              first seen {timeAgo(d.first_seen)}
            </span>
            {d.provider_status === 'closed' && (
              <span className="text-xs text-bluesphere-muted">· closed by provider</span>
            )}
          </div>
          <MdrLink
            target={{ to: 'detection', detectionId: d.detection_id }}
            className="bs-stretched-link block text-left text-base font-semibold hover:underline"
          >
            {d.title}
          </MdrLink>
          {d.asset_name && <p className="text-sm text-bluesphere-muted">{d.asset_name}</p>}
          {(d.asset_groups ?? []).length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1.5">
              {(d.asset_groups ?? []).map((g) => (
                <GroupBadge key={g} slug={g} />
              ))}
            </div>
          )}
        </div>
        <div
          className="text-2xl font-bold tabular-nums"
          style={{ color: SEVERITY_COLORS[d.severity] }}
        >
          {d.score}
        </div>
      </div>

      <div className="mt-3 grid gap-4 md:grid-cols-2">
        <div>
          <h3 className="mb-1 text-xs font-semibold uppercase tracking-wider text-bluesphere-muted">
            AI analysis
          </h3>
          <AiPanel
            tenantId={tenantId}
            detectionId={d.detection_id}
            ai={ai}
            canRequest={canRequest}
            refreshKey={refreshKey}
            compact
          />
        </div>
        <div>
          <h3 className="mb-1 text-xs font-semibold uppercase tracking-wider text-bluesphere-muted">
            Decision
          </h3>
          <TraceView trace={trace} />
        </div>
      </div>
    </div>
  );
}

/** The trace rendered as it was evaluated: each rule considered, the one that fired. */
export function TraceView({ trace }: { trace: DecisionTrace | null }) {
  if (!trace) {
    return <p className="text-sm text-bluesphere-muted">No decision trace was recorded.</p>;
  }
  return (
    <div className="text-sm">
      <ol className="space-y-1">
        {trace.entries.map((e, i) => {
          const fired = e.rule_id === trace.fired_rule_id;
          return (
            <li key={`${e.rule_id}-${i}`} className="flex items-start gap-2">
              <span
                aria-hidden="true"
                className="mt-1.5 inline-block h-2 w-2 shrink-0 rounded-full"
                style={{ backgroundColor: fired ? '#0E9F6E' : '#CBD2DC' }}
              />
              <span className={fired ? 'font-semibold' : 'text-bluesphere-textSecondary'}>
                {e.name}
                <span className="block font-mono text-[11px] text-bluesphere-muted">
                  {e.condition_text} → {e.result ? 'matched' : 'no match'}
                </span>
              </span>
            </li>
          );
        })}
      </ol>
      <p className="mt-2 text-xs text-bluesphere-muted">
        {outcomeLabel(trace.outcome)} by <span className="font-mono">{trace.fired_rule_id}</span> in
        playbook <span className="font-mono">{trace.playbook_id}</span> · class{' '}
        <span className="font-mono">{trace.detection_class}</span>
      </p>
    </div>
  );
}

// ---------- Timeline ----------

/**
 * A note without a status change. Separate from the triage note on
 * purpose: that one travels with a transition, this one stands alone, and
 * an analyst mid-investigation needs the second far more often.
 */
function CommentForm({
  alertRef,
  tenantId,
  onChanged,
}: {
  alertRef: string;
  tenantId: string;
  onChanged?: () => void;
}) {
  const idToken = useMdrToken();
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');

  const mutation = useMutation({
    mutationFn: (text: string) => addAlertComment(tenantId, alertRef, text, idToken),
    onSuccess: () => {
      setNote('');
      void queryClient.invalidateQueries({ queryKey: ['alert', tenantId, alertRef] });
      onChanged?.();
    },
  });

  const text = note.trim();
  const submit = () => {
    if (text && !mutation.isPending) mutation.mutate(text);
  };

  return (
    <form
      className="mb-4 border-b border-bluesphere-border pb-4"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <label className="block">
        <span className="mb-1 block text-xs uppercase tracking-wider text-bluesphere-muted">
          Add a comment
        </span>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          onKeyDown={(e) => {
            // Enter submits; Shift+Enter keeps writing.
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          maxLength={1000}
          rows={2}
          placeholder="What you found, who you asked, what you are waiting on"
          className="w-full resize-y rounded-input border border-bluesphere-border bg-white px-3 py-1.5 text-sm text-bluesphere-textOnLight"
        />
      </label>
      <div className="mt-2 flex items-center justify-between gap-3">
        <span className="text-xs text-bluesphere-muted">
          Recorded on the timeline. The status does not change.
        </span>
        <PillButton
          type="submit"
          variant="secondary"
          size="sm"
          disabled={!text || mutation.isPending}
        >
          {mutation.isPending ? 'Saving…' : 'Add comment'}
        </PillButton>
      </div>
      {mutation.isError && (
        <div className="mt-3">
          <ErrorState title="The comment was not saved" error={mutation.error} />
        </div>
      )}
    </form>
  );
}

/** A playbook rule as the actor of a response event (task 140). */
function ruleActor(raw: unknown): string | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as { playbook_id?: unknown; rule_id?: unknown; version?: unknown };
  if (typeof r.rule_id !== 'string') return null;
  const version = typeof r.version === 'number' && r.version > 0 ? ` (v${r.version})` : '';
  return `playbook ${String(r.playbook_id ?? '')} · rule ${r.rule_id}${version}`;
}

/** One alert event as a timeline row; exported for a host's merged timeline. */
export function AlertTimelineEntry({ event }: { event: AlertEvent }) {
  // A system actor is visually distinct from a person, because "Defender
  // closed this" and "an analyst closed this" are different facts.
  const isSystem = event.actor.startsWith('system');
  const isComment = event.event === 'comment';
  // Response events (Story 19) carry the action and its target.
  const isResponse =
    event.event.startsWith('action_') ||
    event.event.startsWith('release_') ||
    event.event === 'automation_escalated';
  const responseDetail =
    isResponse && typeof event.action === 'string'
      ? `${actionLabel(event.action)}${typeof event.target === 'string' ? ` · ${event.target}` : ''}${
          Array.isArray(event.blocked_by) && event.blocked_by.length
            ? ` · stopped by ${(event.blocked_by as string[]).map(actionLabel).join(', ')}`
            : ''
        }`
      : null;
  // Outreach events (Story 35) carry who answered what, each send and
  // whether it went, and how the exchange ended (deviation 26).
  const outreachLines = event.event.startsWith('outreach_') ? outreachDetail(event) : [];
  const label =
    event.event === 'transition' && event.from_status && event.to_status
      ? `${statusLabel(event.from_status)} → ${statusLabel(event.to_status)}`
      : eventLabel(event.event);
  return (
    <li className="flex gap-3">
      <span
        aria-hidden="true"
        className="mt-1.5 inline-block h-2 w-2 shrink-0 rounded-full"
        style={{ backgroundColor: isSystem ? '#6B7280' : '#76B7CD' }}
      />
      <div className="min-w-0">
        <div className="text-sm font-medium">{label}</div>
        {responseDetail && (
          <div className="text-xs text-bluesphere-textSecondary">{responseDetail}</div>
        )}
        {outreachLines.map((line) => (
          <div key={line} className="wrap-break-word text-xs text-bluesphere-textSecondary">
            {line}
          </div>
        ))}
        {event.detection_id && event.event !== 'transition' && (
          <div className="truncate font-mono text-xs text-bluesphere-textSecondary">
            {event.detection_id}
          </div>
        )}
        {event.note &&
          (isComment ? (
            // The note IS the entry: body text, not a quoted aside.
            <div className="whitespace-pre-wrap wrap-break-word text-sm text-bluesphere-textOnLight">
              {event.note}
            </div>
          ) : (
            <div className="wrap-break-word text-xs text-bluesphere-textSecondary">“{event.note}”</div>
          ))}
        {event.provider_determination && (
          <div className="text-xs text-bluesphere-textSecondary">
            provider determination: {event.provider_determination}
          </div>
        )}
        <div className="text-xs text-bluesphere-muted" title={event.at}>
          {event.actor_email || ruleActor(event.actor_rule) || event.actor} · {timeAgo(event.at)}
        </div>
      </div>
    </li>
  );
}

// ---------- Small pieces ----------

function StatusChip({ status }: { status: AlertStatus }) {
  const closed = status === 'resolved' || status === 'false_positive';
  return (
    <span
      className="bs-chip"
      style={{ backgroundColor: closed ? '#F1F2F4' : '#E5F8F3', color: '#2A3241' }}
    >
      {statusLabel(status)}
    </span>
  );
}

function Field({ label, value, hint }: { label: string; value: ReactNode; hint?: string }) {
  return (
    <div className="min-w-0" title={hint}>
      <dt className="text-xs uppercase tracking-wider text-bluesphere-muted">{label}</dt>
      <dd className="truncate text-bluesphere-textOnLight">{value}</dd>
    </div>
  );
}

function Panel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="bs-card p-5">
      <h2 className="mb-3 text-xl font-bold lg:text-2xl">{title}</h2>
      {children}
    </section>
  );
}
